/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
  message,
} from 'antd';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import { InputSearchPro } from 'components/InputSearch';
import ModalDelete from 'components/ModalDelete';
import LadaNumero from 'components/LadaNumero';
import Select from 'components/Select';
import Direccion from 'components/Direccion';
import formattedValues, { discartNullValues, formatReceived } from 'utils/formatValues';
import { isMutable } from 'utils/estadosGlobales';
import { onError } from 'utils/handlers';
import keyGenerator from 'utils/keyGenerator';
import NumericInput from 'components/NumericInput';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import {
  deleteDependencia,
  getDependencias,
  getHorariosDependencia,
  patchDependencia,
  postDependencia,
} from 'api/configuracion/dependencias';
import {
  deleteDepartamento,
  getDepartamentos,
  getHorariosDepartamento,
  patchDepartamento,
  postDepartamento,
} from 'api/configuracion/departamentos';
import {
  deleteCAAR,
  getCAARs,
  getHorariosCAAR,
  patchCAAR,
  postCAAR,
} from 'api/configuracion/caar';
import BusquedaAvanzadaCajeros from 'components/BusquedaAvanzadaCajeros';
import { getUsuarios, getUsuario } from 'api/usuarios/usuarios';
import { getTiposDeOficinasDeAtencion } from 'api/tramites';
import { getTiposDePresupuestos } from 'api/configuracion';
import { getModelPermission } from 'utils/permission/index';
import HorariosUR, { model as horariosModel, catalogsModels as catalogsModelsHorario } from './HorariosUR';

const { TabPane } = Tabs;

const cruds = {
  dependencias: {
    get: getDependencias,
    post: postDependencia,
    patch: patchDependencia,
    delete: deleteDependencia,
    getHorarios: getHorariosDependencia,
  },
  departamentos: {
    get: getDepartamentos,
    post: postDepartamento,
    patch: patchDepartamento,
    delete: deleteDepartamento,
    getHorarios: getHorariosDepartamento,
  },
  caar: {
    get: getCAARs,
    post: postCAAR,
    patch: patchCAAR,
    delete: deleteCAAR,
    getHorarios: getHorariosCAAR,
  },
};

const UnidadesResponsables = ({ permission }) => {
  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const [form] = Form.useForm();
  const tiposPresupuestos = useSelector(({ catalogs }) => catalogs.tiposPresupuestos);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [visibleAdvanced, setvisibleAdvanced] = useState(false);

  const [unidadesOperativas, setUnidadesOperativas] = useState([]);
  const [unidadesResponsables, setUnidadesResponsables] = useState([]);
  const [tipoOficina, setTipoOficina] = useState([]);
  const [jefeFound, setJefeFound] = useState([]);

  const [mutable, setMutable] = useState(true);
  const [formAddress, setFormAddress] = useState();
  const [horariosPermission, setHorariosPermission] = useState(null);

  const currentURI = window.location.href.split('/').pop();
  const completeURI = `/configuracion/${currentURI}`;
  const API = cruds[currentURI];

  const basicKeys = ['general', 'horarios'];
  const complexForms = [{ key: 'direccion', formInstance: formAddress, permission: addressPermission }];

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setJefeFound([]);
    setMutable(true);
  };

  const authorizedFilter = (e) => e.estados_globales === 4;

  const propNames = {
    dependencias: {
      key: 'director_de_la_dependencia',
      title: 'Director de la Dependencia',
    },
    departamentos: {
      key: 'director_del_departamento',
      title: 'Director del Departamento',
    },
    caar: {
      key: 'jefe_de_recaudadora',
      title: 'Jefe de la Recaudadora',
    },
  };

  const resetAndValidateJefe = () => {
    setJefeFound([]);
    setTimeout(() => {
      form.resetFields([propNames?.[currentURI]?.key]);
      form.validateFields([propNames?.[currentURI]?.key]);
    });
  };

  const fetchData = async () => {
    const _data = await API.get();
    setData(_data);
    if (currentURI === 'departamentos') {
      const _dependencias = await cruds.dependencias.get();
      setUnidadesOperativas(_dependencias.filter(authorizedFilter));
    } else {
      const _departamentos = await cruds.departamentos.get();
      setUnidadesResponsables(_departamentos.filter(authorizedFilter));
    }
  };

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const _horariosPermission = getModelPermission(horariosModel, catalogsModelsHorario);
      if (mounted) {
        setHorariosPermission(_horariosPermission);
      }
    };

    const fetchAll = async () => {
      setLoading(true);
      const _tiposDeOficinas = await getTiposDeOficinasDeAtencion();
      if (!tiposPresupuestos?.length) {
        await getTiposDePresupuestos();
      }
      const _data = await API.get();
      if (currentURI === 'departamentos') {
        const _dependencias = await cruds.dependencias.get();
        // eslint-disable-next-line no-unused-expressions
        mounted && setUnidadesOperativas(_dependencias.filter(authorizedFilter));
      }
      if (currentURI === 'caar') {
        const _departamentos = await cruds.departamentos.get();
        // eslint-disable-next-line no-unused-expressions
        mounted && setUnidadesResponsables(_departamentos.filter(authorizedFilter));
      }
      if (mounted) {
        setTipoOficina(_tiposDeOficinas);
        setData(_data);
        setLoading(false);
      }
    };
    validatePermission();
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const busquedaJefe = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q_jefe']);
      const { q_jefe: email } = form.getFieldsValue();
      if (email) {
        const _usuarios = await getUsuarios({ email });
        if (_usuarios.length === 1) {
          form.setFieldsValue({
            [propNames?.[currentURI]?.key]: _usuarios[0].id,
          });
          setJefeFound(_usuarios.map((e) => ({
            ...e,
            nombre_completo: `${e?.first_name || ''} ${e?.last_name || ''} ${e?.second_last_name || ''}`,
          })));
        } else {
          message.warn('No se encontraron coincidencias.');
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickEdit = async (responseData = null) => {
    const [key] = selectedRowKeys;
    const match = responseData || data.find((i) => i.id === key);
    match.dependencia = match.dependencia?.id || match.dependencia;
    match.departamento = match.departamento?.id || match.departamento;
    setMutable(isMutable(match));
    form.resetFields();
    if (match?.[propNames?.[currentURI]?.key]) {
      const res = await getUsuario(match?.[propNames?.[currentURI]?.key]);
      form.setFieldsValue({ [propNames?.[currentURI]?.key]: res.id });
      const userFound = {
        ...res,
        nombre_completo: `${res?.first_name} ${res?.last_name
          || ''} ${res?.second_last_name || ''}`,
      };
      setJefeFound([userFound]);
    }
    setTimeout(() => {
      const values = discartNullValues(formatReceived(match));
      if (currentURI === 'departamentos') {
        form.setFieldsValue({
          ...values,
          unidad_operativa: values.unidad_operativa.id,
        });
      }
      if (currentURI === 'caar') {
        form.setFieldsValue({
          ...values,
          unidad_responsable: values.unidad_responsable.id,
        });
      }
      if (currentURI === 'dependencias') {
        form.setFieldsValue(formatReceived(values));
      }
      setVisible(true);
    });
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = formattedValues(form.getFieldsValue());
      const [key] = selectedRowKeys;
      const response = await (key ? API.patch(key, values) : API.post(values));
      if (response) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([response.id]);
          }
          setTimeout(() => onClickEdit(response));
        } else {
          onCancel();
        }
        setLoading(false);
        return response;
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
    return null;
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await API.delete(key);
    if (deleted) {
      onCancel();
      setVisibleAlert(false);
      await fetchData();
    }
    setLoading(false);
  };

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    clave: [
      requiredRule,
      {
        validator: async (rule, val) => {
          const value = val?.toString();
          const { unidad_operativa, unidad_responsable } = form.getFieldsValue();
          if (value && !selectedRowKeys.length) {
            if (unidad_operativa) {
              let { clave } = unidadesOperativas.find((e) => e.id === unidad_operativa) || {};
              clave = clave?.toString();
              if (value.length <= clave?.length) {
                throw new Error('La clave debe tener una longitud mayor a la clave de la Dependencia');
              } else if (value.substr(0, clave.length) !== clave) {
                throw new Error('La clave debe comenzar con la clave de la Dependencia');
              }
            } else if (unidad_responsable) {
              let { clave } = unidadesResponsables.find((e) => e.id === unidad_responsable) || {};
              clave = clave?.toString();
              if (value.length <= clave?.length) {
                throw new Error('La clave debe tener una longitud mayor a la clave del Departamento');
              } else if (value.substr(0, clave.length) !== clave) {
                throw new Error('La clave debe comenzar con la clave del Departamento');
              }
            }
          }
        },
      },
      {
        validator: async (rule, val) => {
          const clone = [...data];
          const match = clone.find((e) => e.clave === val);
          if (match && !selectedRowKeys.length) {
            throw new Error('La clave ya se ha dado de alta');
          }
        },
      },
    ],
    correo_electronico: [{ type: 'email' }],
    portal_web: [
      {
        type: 'url',
        message: 'Ingrese una URL válida',
      },
    ],
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 100,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 250,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (val) => (estadosGlobales.find((item) => item.id === val).descripcion),
    },
  ];

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    if (currentURI === 'departamentos') {
      form.setFieldsValue(formatReceived({
        ...record,
        unidad_operativa: record.unidad_operativa.id,
      }));
    }
    if (currentURI === 'caar') {
      form.setFieldsValue(formatReceived({
        ...record,
        unidad_responsable: record.unidad_responsable.id,
      }));
    }
    if (currentURI === 'dependencias') {
      form.setFieldsValue(formatReceived({
        ...record,
      }));
    }
  };

  const onClickAdd = () => {
    onCancel();
    if (currentURI === 'dependencias') {
      keyGenerator(data, form);
    }
    setVisible(true);
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    const match = data.find((i) => i.id === key);
    if (match.estados_globales < 4) {
      setVisibleAlert(true);
    } else {
      const status = match.estados_globales === 4 ? 'autorizados' : 'cancelados';
      message.warn(`No se pueden eliminar registros ${status}`);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const getTitle = (pluralize = false) => {
    let output = '';
    switch (currentURI) {
      case 'dependencias':
        output = pluralize ? 'Dependencias' : 'Dependencia';
        break;
      case 'departamentos':
        output = pluralize ? 'Departamentos' : 'Departamento';
        break;
      default:
        output = pluralize ? 'CAARs' : 'CAAR';
        break;
    }

    return output;
  };

  const showMsg = (key) => {
    message.info({
      content: (
        <>
          <br />
          <Row style={{ width: '100%' }}>
            Los cambios que hayan sido realizados serán descartados.
          </Row>
          <Row align="middle" style={{ width: '100%' }}>
            ¿Desea continuar?
            <Button
              type="link"
              onClick={() => {
                setCurrentTabKey(key);
                message.destroy();
                onClickEdit();
              }}
            >
              Si
            </Button>
            <Button
              type="link"
              onClick={() => message.destroy()}
            >
              No
            </Button>
          </Row>
        </>
      ),
      duration: 0,
    });
  };

  const onChange = (id) => {
    if (id) {
      const parent = (currentURI === 'departamentos' ? unidadesOperativas : unidadesResponsables)
        .find((e) => e.id === id);
      keyGenerator(data, form, { filter: { parent } });
    } else {
      form.resetFields(['id']);
    }
  };

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys, complexForms, currentTabKey, setCurrentTabKey, showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(null),
              onClickDelete,
            }}
            rowKey="id"
            permission={permission}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} ${getTitle()}`}
            extra={(
              <FormSubmitControls
                onFinish={getOnFinish()}
                onCancel={onCancel}
                allowSaveAndContinue
                allowAuthorize
                allowCancel
                mutable={mutable}
                baseURI={completeURI}
                selectedRowKeys={selectedRowKeys}
                callback={(estados_globales) => {
                  const [key] = selectedRowKeys;
                  const normalized = [...data]
                    .map((e) => (e.id === key ? { ...e, estados_globales } : e));
                  setMutable(false);
                  setData(normalized);
                  const match = normalized.find((e) => e.id === key);
                  onClickEdit(match);
                }}
                permission={permission}
              />
            )}
            bordered={false}
            bodyStyle={{ padding: 5, overflow: 'hidden' }}
          >
            <Tabs
              onChange={mutable ? onChangeTabKey : setCurrentTabKey}
              activeKey={currentTabKey}
              type="card"
            >
              <TabPane tab="General" key="general" forceRender>
                <Form
                  layout="vertical"
                  form={form}
                  name="general"
                  onFinish={mutable ? () => onFinish() : null}
                  scrollToFirstError
                  initialValues={{ estados_globales: 1, es_para_multas: false }}
                >
                  <Row gutter={10}>
                    {currentURI === 'departamentos' && (
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="unidad_operativa"
                          rules={rules.required}
                          label="Dependencia"
                          hasFeedback
                        >
                          <Select
                            keyLabelRender
                            dataSource={unidadesOperativas}
                            disabled={!mutable || !!selectedRowKeys.length}
                            onChange={onChange}
                          />
                        </Form.Item>
                      </Col>

                    )}
                    {currentURI === 'caar' && (
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="unidad_responsable"
                          rules={rules.required}
                          label="Departamento"
                          hasFeedback
                        >
                          <Select
                            keyLabelRender
                            dataSource={unidadesResponsables}
                            disabled={!mutable || !!selectedRowKeys.length}
                            onChange={onChange}
                          />
                        </Form.Item>
                      </Col>

                    )}

                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="clave"
                        rules={rules.clave}
                        label="Clave"
                        hasFeedback
                        normalize={(val) => val?.toUpperCase()}
                      >
                        <Input
                          className="upper"
                          disabled={!mutable || !!selectedRowKeys.length}
                          allowClear
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="descripcion"
                        rules={rules.required}
                        label="Descripción"
                        hasFeedback
                      >
                        <Input allowClear disabled={!mutable || !!selectedRowKeys.length} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="correo_electronico"
                        rules={rules.correo_electronico}
                        label="Correo Electrónico"
                        hasFeedback
                      >
                        <Input allowClear disabled={!mutable} />
                      </Form.Item>
                    </Col>
                    <InputSearchPro
                      label={propNames?.[currentURI]?.title}
                      tooltip="Búsqueda de Director de la Dependencia por Email"
                      name="q_jefe"
                      rules={rules.correo_electronico}
                      onPressEnter={() => busquedaJefe()}
                      onClickAdvanced={() => setvisibleAdvanced(true)}
                      disabled={!mutable}
                      breakPoints={{
                        xs: 24,
                        sm: 24,
                        md: 24,
                        lg: 16,
                      }}
                      inputProps={{
                        onPressEnter: () => busquedaJefe(),
                        onChange: ({ target: { value } }) => {
                          if (!value) {
                            resetAndValidateJefe();
                          }
                        },
                      }}
                      resultProps={{
                        name: propNames?.[currentURI]?.key,
                        label: propNames?.[currentURI]?.title,
                        dataSource: jefeFound,
                        labelProp: 'nombre_completo',
                        onClear: resetAndValidateJefe,
                        breakPoints: {
                          md: 16,
                        },
                      }}
                    />

                    <LadaNumero
                      form={form}
                      disabled={!mutable}
                    />
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="portal_web"
                        rules={rules.portal_web}
                        label="Portal Web"
                        hasFeedback
                      >
                        <Input allowClear disabled={!mutable} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="tiene_equipo_de_computo"
                        label="¿Tiene Equipo de Computo?"
                      >
                        <Select trueFalse disabled={!mutable} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="tiene_conexion_a_internet"
                        label="¿Tiene Conexión a Internet?"
                      >
                        <Select trueFalse disabled={!mutable} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="tiene_area_de_espera"
                        label="¿Tiene Área de Espera?"
                      >
                        <Select trueFalse disabled={!mutable} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <NumericInput
                        name="extension"
                        label="Extensión"
                        maxLength={5}
                        disabled={!mutable}
                      />
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="tipo_de_oficina"
                        label="Tipo de Oficina"
                      >
                        <Select dataSource={tipoOficina} disabled={!mutable} />
                      </Form.Item>
                    </Col>
                    {currentURI === 'dependencias' && (
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="es_para_multas"
                          label="¿Es para multas?"
                        >
                          <Select trueFalse disabled={!mutable} />
                        </Form.Item>
                      </Col>
                    )}
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="estados_globales"
                        label="Estado"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select dataSource={estadosGlobales} disabled />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              {addressPermission?.view && (
                <TabPane
                  tab="Dirección"
                  key="direccion"
                  forceRender
                  disabled={!selectedRowKeys.length}
                >
                  <Direccion
                    currentURI={completeURI}
                    parentID={selectedRowKeys[0]}
                    setLoading={setLoading}
                    setFormAddress={setFormAddress}
                    setCurrentTabKey={setCurrentTabKey}
                    addressPropName="direccion"
                    permission={addressPermission}
                    callback={(values, _continue) => {
                      if (!_continue) {
                        onCancel();
                      } else {
                        const [key] = selectedRowKeys;
                        const clone = [...data];
                        const mappedData = clone.map((e) => (e.id === key
                          ? ({ ...e, direccion: values?.id }) : e));
                        setData([...mappedData]);
                      }
                    }}
                    mutable={mutable}
                  />
                </TabPane>
              )}
              {horariosPermission?.view && (
                <TabPane
                  tab="Horarios"
                  key="horarios"
                  disabled={!selectedRowKeys.length}
                >
                  <HorariosUR
                    API={API}
                    selectedRowKeys={selectedRowKeys}
                    mutable={mutable}
                    permission={horariosPermission}
                  />
                </TabPane>
              )}
            </Tabs>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content={`${getTitle()} ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
        <BusquedaAvanzadaCajeros
          visible={visibleAdvanced}
          setVisible={setvisibleAdvanced}
          callback={(selected) => {
            const _selected = {
              ...selected,
              nombre_completo: `${selected?.first_name} ${selected?.last_name
                || ''} ${selected?.second_last_name || ''}`,
            };
            setJefeFound([_selected]);
            if (currentURI === 'dependencias') {
              form.setFieldsValue({
                director_de_la_dependencia: selected.id,
              });
            }
            if (currentURI === 'departamentos') {
              form.setFieldsValue({
                director_del_departamento: selected.id,
              });
            }
            if (currentURI === 'caar') {
              form.setFieldsValue({
                jefe_de_recaudadora: selected.id,
              });
            }
          }}
          dataExterno={[]}
          setDataExterno={() => { }}
        />
      </Spin>
    </Row>
  );
};

export default UnidadesResponsables;
